import { useState } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from './constants';

export default function FAQsTab({ faqs, onRefresh }) {
  const [editingFaq, setEditingFaq] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ question: '', answer: '', category: 'general' });

  const openDialog = (faq = null) => {
    if (faq) {
      setEditingFaq(faq);
      setForm({ question: faq.question, answer: faq.answer || '', category: faq.category || 'general' });
    } else {
      setEditingFaq(null);
      setForm({ question: '', answer: '', category: 'general' });
    }
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.question) {
      toast.error('Please enter a question');
      return;
    }
    try {
      if (editingFaq) {
        await axios.put(`${API}/faqs/${editingFaq.id}`, form);
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API}/faqs`, form);
        toast.success('FAQ created');
      }
      setShowDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to save FAQ');
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/faqs/${id}`);
      toast.success('FAQ deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const togglePublished = async (faq) => {
    try {
      await axios.put(`${API}/faqs/${faq.id}`, { is_published: !faq.is_published });
      toast.success(faq.is_published ? 'FAQ unpublished' : 'FAQ published');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update FAQ');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>FAQs ({faqs.length})</CardTitle>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()} className="gap-2">
              <Plus className="w-4 h-4" /> Add FAQ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Question *</Label>
                <Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
              </div>
              <div>
                <Label>Answer</Label>
                <Textarea rows={4} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </div>
              <Button onClick={save} className="w-full">{editingFaq ? 'Update' : 'Create'} FAQ</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {faqs.map((faq) => (
            <div key={faq.id} className={`p-4 rounded-lg ${faq.is_published ? 'bg-muted/50' : 'bg-muted/20 border border-dashed'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-bold">{faq.question}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{faq.answer || 'No answer yet'}</p>
                  <span className="text-xs text-muted-foreground">{faq.category} • {faq.is_published ? 'Published' : 'Draft'}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => togglePublished(faq)}>
                    {faq.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openDialog(faq)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="destructive" size="sm" onClick={() => deleteItem(faq.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
